import { Hero } from '@/sections/Hero';
import { AudiencePaths } from '@/sections/AudiencePaths';
import { Offers } from '@/sections/Offers';
import { ProblemGrid } from '@/sections/ProblemGrid';
import { About } from '@/sections/About';
import { Method } from '@/sections/Method';
import { LatestInsights } from '@/sections/LatestInsights';
import { Projects } from '@/sections/Projects';
import { Testimonials } from '@/sections/Testimonials';
import { CTA } from '@/sections/CTA';
import { useRouteMeta } from '@/hooks/useRouteMeta';
import { useConfig } from '@/lib/siteData';
import { offersSection as defaultOffersSection } from '@/config-defaults';

/**
 * Homepage composition.
 *
 * The productized offers now sit directly under the audience pathways, above
 * the biography — previously `<Offers>` rendered only on /services, so the
 * homepage never showed what you can actually buy. The services grid moved off
 * the homepage entirely; it still lives on /services, which the pathways and
 * the nav both link to.
 *
 * ProblemGrid sits between the pathways and the offers, per the spec's section
 * order — pathways answer "who are you", the grid answers "is my problem one
 * you work on", the offers answer "what can I buy". It renders as text-forward
 * tiles rather than a third card grid, which would have put three near-identical
 * slabs in a row.
 *
 * Surface alternation matters at these seams: AudiencePaths is gray, ProblemGrid
 * elevated, so Offers goes back to gray. Two adjacent sections on the same
 * background merge into one visual slab.
 *
 * `index` drives the "01 —" eyebrow on each section and is a hardcoded literal,
 * so the numbers have to stay contiguous when sections move.
 */
export default function Home() {
  useRouteMeta({
    titleAbsolute: 'Chinedu Koggu — Fintech & Digital Infrastructure Strategist',
    description: 'Fintech strategist, payment systems architect, and CTO helping African organizations design compliant, scalable digital infrastructure.',
  });

  // Same admin-editable row /services reads, so the heading and intro are
  // authored once rather than the homepage falling back to the component's
  // hardcoded copy (whose "Five productized offers" wording would go stale the
  // moment an offer is added or hidden).
  const offersSectionCfg = useConfig('offers_section', defaultOffersSection);

  return (
    <>
      <Hero />
      <AudiencePaths index={1} />
      <ProblemGrid index={2} />
      {offersSectionCfg.visible !== false && (
        <Offers
          index={3}
          surface="gray"
          // Only the first few offers on Home — all 10 cards here made the
          // mobile homepage ~19,000px tall. Offers renders a "See all
          // engagement models" link to /services when truncated.
          limit={3}
          heading={offersSectionCfg.heading ?? defaultOffersSection.heading}
          intro={offersSectionCfg.intro ?? defaultOffersSection.intro}
        />
      )}
      <About index={4} />
      <Method index={5} />
      <Projects index={6} />
      <Testimonials index={7} />
      <LatestInsights index={8} />
      <CTA index={9} />
    </>
  );
}
